import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { publicApi, resolveImageUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Mail, ArrowRight } from "lucide-react";

export default function UsedMachinesPage() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchUsed = async () => {
            try {
                const res = await publicApi.getProducts({ category: "used-machines" });
                setProducts(res.data);
            } catch (err) {
                console.error("Failed to fetch used machines:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchUsed();
    }, []);

    return (
        <div className="min-h-[70vh]" data-testid="used-machines-page">
            <div className="bg-card border-b border-border">
                <div className="container mx-auto px-6 lg:px-12 py-12 lg:py-20">
                    <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">Pre-Owned Equipment</p>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-foreground tracking-tight mb-4">
                        Used Machines
                    </h1>
                    <p className="text-muted-foreground text-base sm:text-lg max-w-2xl">
                        Quality-checked, pre-owned dry ice blasting equipment at reduced prices. Every used machine is inspected and serviced before sale.
                    </p>
                </div>
            </div>

            <div className="container mx-auto px-6 lg:px-12 py-12 lg:py-20">
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="bg-card border border-border h-80 animate-pulse" />
                        ))}
                    </div>
                ) : products.length > 0 ? (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {products.map(product => (
                                <Link
                                    key={product.id}
                                    to={`/products/${product.id}`}
                                    className="group bg-card border border-border hover:border-primary/50 transition-all duration-300"
                                    data-testid={`used-card-${product.id}`}
                                >
                                    <div className="relative aspect-square bg-muted/30 overflow-hidden flex items-center justify-center">
                                        {product.images?.[0] ? (
                                            <img
                                                src={resolveImageUrl(product.images[0])}
                                                alt={product.display_title}
                                                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                                            />
                                        ) : (
                                            <div className="text-muted-foreground text-sm">No image</div>
                                        )}
                                        {product.price_text && (
                                            <div className="absolute top-3 right-3 bg-primary text-primary-foreground px-3 py-1 font-mono text-xs font-bold uppercase">
                                                {product.price_text.split('—')[0].trim()}
                                            </div>
                                        )}
                                    </div>
                                    <div className="p-5 border-t border-border">
                                        <p className="font-mono text-xs uppercase tracking-widest text-primary mb-2">
                                            Manufactured by {product.manufacturer} · Official Sales & Service by {product.sales_service_provider || "Dry Ice Bulls"}
                                        </p>
                                        <h3 className="font-heading text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                                            {product.display_title}
                                        </h3>
                                        <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                                            {product.short_description}
                                        </p>
                                        <div className="flex items-center text-primary text-sm font-mono uppercase tracking-wider">
                                            View Details <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>

                        <div className="mt-12 bg-card border border-border p-8 text-center">
                            <p className="text-muted-foreground text-sm mb-4">
                                Interested in a used machine? Contact us for the purchase process and availability.
                            </p>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                <Link to="/quote">
                                    <Button
                                        className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider px-8"
                                        data-testid="used-machines-quote-btn"
                                    >
                                        Contact for Purchase
                                    </Button>
                                </Link>
                                <a href="mailto:sales@dryicemarketplace.com" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm">
                                    <Mail className="h-4 w-4" />
                                    sales@dryicemarketplace.com
                                </a>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="bg-card border border-border p-8 lg:p-12 text-center max-w-2xl mx-auto">
                        <div className="w-16 h-16 bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-6">
                            <span className="font-heading font-black text-primary text-2xl">!</span>
                        </div>
                        <h2 className="font-heading text-lg font-bold text-foreground mb-3">
                            Inventory Updated Regularly
                        </h2>
                        <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                            Our used machine inventory changes frequently. Contact us directly to learn about currently available pre-owned equipment, pricing, and condition reports.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Link to="/quote">
                                <Button
                                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-bold tracking-wider px-8"
                                    data-testid="used-machines-quote-btn"
                                >
                                    Inquire About Used Machines
                                </Button>
                            </Link>
                            <a href="mailto:sales@dryicemarketplace.com" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm">
                                <Mail className="h-4 w-4" />
                                sales@dryicemarketplace.com
                            </a>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
